import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import axios from 'axios';
import { Mail, BookOpen, Heart, ChevronRight, Feather, Quote } from 'lucide-react';

import { API } from '../config';

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [teaserLetters, setTeaserLetters] = useState([]);
  const [selectedLetter, setSelectedLetter] = useState(null);

  useEffect(() => {
    fetchTeaserLetters();
  }, []);

  const fetchTeaserLetters = async () => {
    try {
      const response = await axios.get(`${API}/letters/teaser`);
      setTeaserLetters(response.data);
      if (response.data.length > 0) {
        setSelectedLetter(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching teaser letters:', error);
    }
  };

  const handleReadFirst = () => {
    if (selectedLetter) {
      navigate(`/letter/${selectedLetter.week_number}`);
    }
  };

  return (
    <div className="min-h-screen bg-parchment">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-parchment/80 backdrop-blur-sm border-b border-[#E5E0D0]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Feather className="w-6 h-6 text-wine" />
            <span className="font-playfair text-xl text-charcoal tracking-tight">Unspoken Hearts</span>
          </Link>
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <>
                <Link to="/archive" className="font-lora text-charcoal-muted hover:text-charcoal transition-colors" data-testid="archive-link">
                  Archive
                </Link>
                <Link to="/account" className="font-lora text-charcoal-muted hover:text-charcoal transition-colors" data-testid="account-link">
                  Account
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" className="font-lora text-charcoal-muted hover:text-charcoal transition-colors" data-testid="login-link">
                  Sign In
                </Link>
                <Button 
                  onClick={() => navigate('/register')}
                  className="btn-primary text-sm py-2 px-6"
                  data-testid="get-started-btn"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-fade-in">
              <div className="space-y-6">
                {/* Company Logo */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_ink-nostalgia/artifacts/xhguxwkc_34799.png"
                  alt="Letters of Unspoken Hearts - Moments Preserved in Ink"
                  className="w-full max-w-lg"
                  data-testid="company-logo"
                />
                <p className="font-lora text-xl text-charcoal-muted italic">
                  "Read the words hearts never spoke."
                </p>
              </div>
              
              <p className="font-lora text-lg text-charcoal-muted leading-relaxed max-w-xl">
                A 52-week journey through love letters never sent, diaries never shared, 
                and words that changed everything. Experience an intimate story delivered 
                to your heart, one letter at a time.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={handleReadFirst}
                  className="btn-primary text-base"
                  data-testid="read-first-letter-btn"
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  Read Your First Letter
                </Button>
                <Button 
                  onClick={() => navigate(isAuthenticated ? '/subscription' : '/register')}
                  className="btn-secondary text-base"
                  data-testid="subscribe-btn"
                >
                  Subscribe for $7/month
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </div>

              <div className="pt-4">
                <p className="font-dancing text-xl text-sepia mb-4">Summer, 1978</p>
                <div className="flex items-center gap-8">
                  <div className="text-center">
                    <p className="font-playfair text-3xl text-charcoal">52</p>
                    <p className="font-lora text-sm text-charcoal-muted">Weekly Letters</p>
                  </div>
                  <div className="w-px h-12 bg-[#E5E0D0]"></div>
                  <div className="text-center">
                    <p className="font-playfair text-3xl text-charcoal">2</p>
                    <p className="font-lora text-sm text-charcoal-muted">Perspectives</p>
                  </div>
                  <div className="w-px h-12 bg-[#E5E0D0]"></div>
                  <div className="text-center">
                    <p className="font-playfair text-3xl text-charcoal">1</p>
                    <p className="font-lora text-sm text-charcoal-muted">Love Story</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative animate-fade-in delay-200">
              <div className="relative z-10">
                <img 
                  src="https://images.unsplash.com/photo-1593043927112-08289c3f1b64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHx2aW50YWdlJTIwbG92ZSUyMGxldHRlciUyMHBhcGVyJTIwdGV4dHVyZXxlbnwwfHx8fDE3NzI2NzY5MDF8MA&ixlib=rb-4.1.0&q=85"
                  alt="Vintage paper texture"
                  className="w-full h-[500px] object-cover rounded-sm shadow-lifted-paper"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 z-20">
                <div className="wax-seal">
                  <Heart className="w-6 h-6 text-parchment" />
                </div>
              </div>
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-rose-muted/20 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Teaser Letters Section */}
      <section className="py-20 px-6 bg-parchment-paper">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-fade-in">
            <p className="font-dancing text-xl text-sepia mb-2">Begin your journey</p>
            <h2 className="font-playfair text-4xl md:text-5xl text-charcoal">Free Preview Letters</h2>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Letter Selection */}
            <div className="space-y-4">
              {teaserLetters.map((letter, index) => (
                <Card 
                  key={letter.letter_id}
                  onClick={() => setSelectedLetter(letter)}
                  className={`p-6 cursor-pointer transition-all duration-300 ${
                    selectedLetter?.letter_id === letter.letter_id 
                      ? 'bg-parchment border-sepia shadow-lifted-paper' 
                      : 'bg-parchment/50 border-[#E5E0D0] hover:bg-parchment hover:shadow-soft-paper'
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                  data-testid={`teaser-letter-${letter.week_number}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-dancing text-sepia text-sm">{letter.date}</p>
                      <h3 className="font-playfair text-lg text-charcoal mt-1">{letter.title}</h3>
                      <p className="font-lora text-sm text-charcoal-muted mt-2">
                        Week {letter.week_number} · {letter.pov}'s letter
                      </p>
                    </div>
                    <Mail className="w-5 h-5 text-rose-muted flex-shrink-0" />
                  </div>
                </Card>
              ))}
            </div>

            {/* Letter Preview */}
            <div className="lg:col-span-2">
              {selectedLetter && (
                <Card className="card-letter h-full animate-fade-in" data-testid="letter-preview">
                  <div className="border-b border-[#E5E0D0] pb-6 mb-6">
                    <p className="font-dancing text-xl text-sepia">{selectedLetter.date}</p>
                    <h3 className="font-playfair text-2xl text-charcoal mt-2">{selectedLetter.title}</h3>
                    <p className="font-lora text-sm text-charcoal-muted mt-2">
                      Written by {selectedLetter.pov} · Week {selectedLetter.week_number}
                    </p>
                  </div>
                  
                  <div className="letter-content prose prose-lg">
                    {selectedLetter.content.split('\n\n').slice(0, 3).map((paragraph, idx) => (
                      <p key={idx} className="font-lora text-charcoal leading-relaxed mb-4">
                        {paragraph}
                      </p>
                    ))}
                    <p className="font-lora text-charcoal-muted italic">...</p>
                  </div>

                  <div className="mt-8 pt-6 border-t border-[#E5E0D0]">
                    <Button 
                      onClick={() => navigate(`/letter/${selectedLetter.week_number}`)}
                      className="btn-secondary w-full justify-center"
                      data-testid="continue-reading-btn"
                    >
                      Continue Reading
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 animate-fade-in" data-testid="feature-weekly">
              <div className="w-16 h-16 mx-auto mb-6 bg-rose-muted/20 rounded-full flex items-center justify-center">
                <Mail className="w-8 h-8 text-wine" />
              </div>
              <h3 className="font-playfair text-xl text-charcoal mb-3">Weekly Delivery</h3>
              <p className="font-lora text-charcoal-muted">
                Every week, a new letter arrives. A new chapter unfolds. A new emotion awakens.
              </p>
            </div>

            <div className="text-center p-8 animate-fade-in delay-100" data-testid="feature-immersive">
              <div className="w-16 h-16 mx-auto mb-6 bg-rose-muted/20 rounded-full flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-wine" />
              </div>
              <h3 className="font-playfair text-xl text-charcoal mb-3">Immersive Reading</h3>
              <p className="font-lora text-charcoal-muted">
                No distractions. Just you, the words, and the quiet intimacy of a private letter.
              </p>
            </div>

            <div className="text-center p-8 animate-fade-in delay-200" data-testid="feature-emotional">
              <div className="w-16 h-16 mx-auto mb-6 bg-rose-muted/20 rounded-full flex items-center justify-center">
                <Heart className="w-8 h-8 text-wine" />
              </div>
              <h3 className="font-playfair text-xl text-charcoal mb-3">Emotional Journey</h3>
              <p className="font-lora text-charcoal-muted">
                Experience the full spectrum of love—hope, longing, joy, and the bittersweet.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 bg-parchment-subtle relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://images.unsplash.com/photo-1698045365336-bc1820d43470?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwzfHxkcmllZCUyMHJvc2UlMjBmbG93ZXIlMjByb21hbnRpYyUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3NzI2NzY5MDJ8MA&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center animate-fade-in">
            <Quote className="w-12 h-12 text-rose-muted mx-auto mb-8" />
            <blockquote className="font-playfair text-2xl md:text-3xl text-charcoal leading-relaxed italic mb-8">
              "These letters feel like discovering a hidden love story in an attic trunk. 
              Each week, I wait for the next chapter with bated breath."
            </blockquote>
            <p className="font-lora text-charcoal-muted">— A devoted reader</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center animate-fade-in">
          <h2 className="font-playfair text-4xl md:text-5xl text-charcoal mb-6">
            Begin Your Journey
          </h2>
          <p className="font-lora text-lg text-charcoal-muted mb-8 max-w-xl mx-auto">
            52 letters. One year. A love story that will stay with you forever.
          </p>
          <Button 
            onClick={() => navigate(isAuthenticated ? '/subscription' : '/register')}
            className="btn-primary text-lg px-12 py-4"
            data-testid="cta-subscribe-btn"
          >
            Subscribe for $7/month
          </Button>
          <p className="font-lora text-sm text-charcoal-light mt-4">
            Cancel anytime. First 3 letters free.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#E5E0D0]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <Feather className="w-5 h-5 text-wine" />
              <span className="font-playfair text-lg text-charcoal">Letters of Unspoken Hearts</span>
            </div>
            <p className="font-dancing text-lg text-sepia">"Moments preserved in ink"</p>
            <div className="flex items-center gap-6 font-lora text-sm text-charcoal-muted">
              <a href="#" className="hover:text-charcoal transition-colors">Privacy</a>
              <a href="#" className="hover:text-charcoal transition-colors">Terms</a>
              <span>© 2026</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
